/**
 * SupportChat — iter-104 — floating Help bubble (bottom-right).
 *
 * Tap the bubble to open a chat panel powered by Claude Haiku 4.5
 * (server-side). Knows the product (export, pricing, packs, cancel)
 * and escalates billing / refund / account-deletion to email.
 *
 * Hidden on /c/:slug (public viewer) so the gift moment stays clean.
 */
import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useLocation } from "react-router-dom";
import { MessageCircle, X, Send } from "lucide-react";
import axios from "axios";
import { API } from "../lib/api";
import { track } from "../lib/track";

const STARTER = {
    role: "assistant",
    content: "hey — i can help with exports, pricing, cancelling, packs, and most things. what's on your mind?",
};

export default function SupportChat() {
    const location = useLocation();
    const [open, setOpen] = useState(false);
    const [messages, setMessages] = useState([STARTER]);
    const [input, setInput] = useState("");
    const [busy, setBusy] = useState(false);
    const sessionRef = useRef(null);
    const scrollRef = useRef(null);

    // Hide on public viewer + auth callback (focus moments).
    const hideOnRoute = /^\/(c\/|auth)/.test(location.pathname);

    useEffect(() => {
        if (!scrollRef.current) return;
        scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }, [messages, open]);

    const toggle = () => {
        const next = !open;
        setOpen(next);
        if (next) track("support_chat_open", { path: location.pathname });
    };

    const send = async () => {
        const text = input.trim();
        if (!text || busy) return;
        const nextHistory = [...messages, { role: "user", content: text }];
        setMessages(nextHistory);
        setInput("");
        setBusy(true);
        track("support_chat_send", {});

        try {
            const { data } = await axios.post(`${API}/support/chat`, {
                message: text,
                // Send only the last 6 turns to match server-side trim.
                history: nextHistory.slice(-7, -1),
                session_id: sessionRef.current || undefined,
            });
            if (data?.session_id) sessionRef.current = data.session_id;
            setMessages((m) => [...m, { role: "assistant", content: data?.reply || "..." }]);
        } catch (e) {
            setMessages((m) => [
                ...m,
                {
                    role: "assistant",
                    content:
                        "i couldn't reach the assistant — please email support@moodcard.app and we'll help directly.",
                },
            ]);
        } finally {
            setBusy(false);
        }
    };

    const onKey = (e) => {
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            send();
        }
    };

    if (hideOnRoute) return null;

    return (
        <>
            {/* Bubble */}
            <motion.button
                type="button"
                onClick={toggle}
                whileTap={{ scale: 0.92 }}
                data-testid="support-chat-bubble"
                className="fixed bottom-24 right-4 z-40 h-11 w-11 rounded-full bg-app text-app-inverse shadow-[0_10px_30px_-8px_rgba(0,0,0,0.6)] border border-app/30 flex items-center justify-center hover:scale-105 transition-transform"
                aria-label={open ? "Close help" : "Open help"}
            >
                <AnimatePresence mode="wait">
                    {open ? (
                        <motion.div key="x" initial={{ rotate: -90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: 90, opacity: 0 }} transition={{ duration: 0.18 }}>
                            <X className="w-5 h-5" strokeWidth={1.8} />
                        </motion.div>
                    ) : (
                        <motion.div key="msg" initial={{ rotate: 90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: -90, opacity: 0 }} transition={{ duration: 0.18 }}>
                            <MessageCircle className="w-5 h-5" strokeWidth={1.8} />
                        </motion.div>
                    )}
                </AnimatePresence>
            </motion.button>

            {/* Panel */}
            <AnimatePresence>
                {open && (
                    <motion.div
                        initial={{ y: 24, opacity: 0, scale: 0.96 }}
                        animate={{ y: 0, opacity: 1, scale: 1 }}
                        exit={{ y: 24, opacity: 0, scale: 0.96 }}
                        transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
                        className="fixed z-40 bottom-[8.5rem] right-4 w-[88vw] max-w-[360px] h-[480px] max-h-[70vh] rounded-3xl border border-app/25 bg-surface-1 backdrop-blur-2xl shadow-[0_30px_80px_rgba(0,0,0,0.6)] overflow-hidden flex flex-col"
                        data-testid="support-chat-panel"
                    >
                        <div className="px-5 py-3 border-b border-app/15 flex items-center justify-between">
                            <div>
                                <p className="font-sans text-[10px] uppercase tracking-[0.3em] text-app-3">MoodCard</p>
                                <p className="font-serif italic text-app text-lg leading-tight">help · chat</p>
                            </div>
                            <span className="font-sans text-[10px] text-app-3">usually instant</span>
                        </div>

                        <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
                            {messages.map((m, i) => (
                                <div
                                    key={i}
                                    className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
                                    data-testid={`support-chat-msg-${m.role}`}
                                >
                                    <div
                                        className={`max-w-[80%] px-3.5 py-2 rounded-2xl font-sans text-[13px] leading-relaxed ${
                                            m.role === "user"
                                                ? "bg-cta text-app-inverse rounded-br-md"
                                                : "bg-surface-2 text-app rounded-bl-md"
                                        }`}
                                    >
                                        {m.content.split("\n").map((line, j) => (
                                            <p key={j}>{line}</p>
                                        ))}
                                    </div>
                                </div>
                            ))}
                            {busy && (
                                <div className="flex justify-start">
                                    <div className="bg-surface-2 text-app-3 px-3.5 py-2 rounded-2xl rounded-bl-md font-sans text-[13px] italic">
                                        thinking…
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="border-t border-app/15 p-3 flex items-end gap-2">
                            <textarea
                                value={input}
                                onChange={(e) => setInput(e.target.value)}
                                onKeyDown={onKey}
                                placeholder="ask anything — pricing, exports, cancel…"
                                rows={1}
                                data-testid="support-chat-input"
                                className="flex-1 resize-none rounded-2xl border border-app/20 bg-surface-2 px-3 py-2 font-sans text-[13px] text-app placeholder:text-app-3 focus:outline-none focus:border-app/40 max-h-[80px]"
                            />
                            <button
                                type="button"
                                onClick={send}
                                disabled={busy || !input.trim()}
                                data-testid="support-chat-send"
                                className="h-9 w-9 shrink-0 rounded-full bg-app text-app-inverse flex items-center justify-center disabled:opacity-40 hover:scale-105 transition-transform"
                                aria-label="Send message"
                            >
                                <Send className="w-4 h-4" strokeWidth={1.8} />
                            </button>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
}
